import { createGlobalStyle } from 'styled-components';

import LatoLight from './assets/fonts/Lato-Light.ttf';
import LatoRegular from './assets/fonts/Lato-Regular.ttf';
import LatoItalic from './assets/fonts/Lato-Italic.ttf';
import LatoBold from './assets/fonts/Lato-Bold.ttf';
import LatoBlack from './assets/fonts/Lato-Black.ttf';

const FONT_FAMILY = 'Lato';

const fonts = [
  { weight: 300, style: 'normal', src: LatoLight },
  { weight: 400, style: 'normal', src: LatoRegular },
  { weight: 400, style: 'italic', src: LatoItalic },
  { weight: 700, style: 'normal', src: LatoBold },
  { weight: 900, style: 'normal', src: LatoBlack },
];

const fontFaces = fonts
  .map(
    ({ weight, style, src }) => `
  @font-face {
    font-family: '${FONT_FAMILY}';
    src: url('${src}') format('truetype');
    font-weight: ${weight};
    font-style: ${style};
    font-display: swap;
  }`
  )
  .join('\n');

const GlobalStyle = createGlobalStyle`
  ${fontFaces}

  :root {
    --c-bg: #0a0a0c;
    --c-surface: #141417;
    --c-surface-2: #1c1c21;
    --c-elevated: #25252b;

    --c-text: #ededf0;
    --c-text-muted: #a0a0aa;
    --c-text-subtle: #6c6c76;

    --c-border: #2a2a31;
    --c-border-strong: #3b3b44;

    --c-accent: #7c6cf0;
    --c-accent-hover: #9486f3;
    --c-accent-subtle: rgba(124, 108, 240, 0.12);

    --c-danger: #f0616d;
    --c-success: #4ed1a1;

    --space-1: 0.25rem;
    --space-2: 0.5rem;
    --space-3: 0.75rem;
    --space-4: 1rem;
    --space-5: 1.5rem;
    --space-6: 2rem;
    --space-7: 3rem;
    --space-8: 4rem;
    --space-9: 6rem;
    --space-10: 8rem;

    --fs-xs: 0.75rem;
    --fs-sm: 0.875rem;
    --fs-base: 1rem;
    --fs-lg: 1.125rem;
    --fs-xl: 1.25rem;
    --fs-2xl: 1.5rem;
    --fs-3xl: 1.875rem;
    --fs-4xl: 2.25rem;
    --fs-5xl: 3rem;

    --fw-light: 300;
    --fw-regular: 400;
    --fw-bold: 700;
    --fw-black: 900;

    --lh-tight: 1.15;
    --lh-base: 1.6;

    --ff-main: '${FONT_FAMILY}', system-ui, sans-serif;

    --radius-sm: 0.25rem;
    --radius-md: 0.5rem;
    --radius-lg: 0.75rem;
    --radius-xl: 1rem;
    --radius-full: 9999px;

    --border: 1px solid var(--c-border);
    --border-strong: 1px solid var(--c-border-strong);

    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.4);
    --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.45);
    --shadow-lg: 0 12px 32px rgba(0, 0, 0, 0.55);
    --shadow-accent: 0 0 0 1px var(--c-accent-subtle),
                     0 8px 24px rgba(124, 108, 240, 0.25);
  }

  *, *::before, *::after {
    box-sizing: border-box; 
    font-family: var(--ff-main);
  }

  * { 
    margin: 0;
    padding: 0;
  }

  html {
    -webkit-text-size-adjust: 100%;
  }

  body {
    min-height: 100vh;
    background-color: var(--c-bg);
    color: var(--c-text);
    font-size: var(--fs-base);
    font-weight: var(--fw-regular);
    line-height: var(--lh-base);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  h1, h2, h3, h4, h5, h6 {
    font-size: inherit;
    font-weight: inherit;
  }

  img, picture, svg, video { 
    display: block;
    max-width: 100%; 
  }

  input, button, textarea, select { 
    font: inherit;
    color: inherit;
  }

  ::placeholder { 
    color: var(--c-text-subtle);
  }

  button {
    cursor: pointer; 
    background: none; 
    border: none; 
    padding: 0; 
  }

  a {
    color: inherit;
    text-decoration: none; 
  }

  ul, ol { 
    list-style: none; 
    padding: 0;
  }

  :focus-visible {
    outline: 2px solid var(--c-accent);
    outline-offset: 2px; 
  }

  ::selection {
    background: var(--c-accent); 
    color: #fff; 
  }
`;

export default GlobalStyle;

